import { DownloadOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Drawer,
  Empty,
  message,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tag,
} from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type SessionLogUpload,
  sessionLogsApi,
} from '@/services/sessionLogsApi';
import { CodexTranscriptView } from './CodexTranscriptView';
import { startSessionLogDownload } from './download';
import { JsonLineView } from './JsonLineView';
import {
  decodeCompleteSessionLog,
  MAX_JSON_LINE_LENGTH,
  type ParsedJsonLine,
  type PreviewLine,
  type PreviewSnapshot,
  parseJsonLine,
} from './preview';

interface PreviewSession {
  upload: SessionLogUpload;
  loading: boolean;
  controller?: AbortController;
}

function errorText(error: unknown, fallback: string) {
  if (!error || typeof error !== 'object') return fallback;
  const value = error as {
    message?: string;
    response?: { data?: { message?: string } };
  };
  return value.response?.data?.message || value.message || fallback;
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
}

function PreviewLines({ snapshot }: { snapshot: PreviewSnapshot }) {
  const [page, setPage] = useState(1);
  // Only parse the visible page. Even logs with many tiny lines have a bounded DOM.
  const lines = useMemo(
    () =>
      snapshot.lines.slice((page - 1) * 100, page * 100).map((line) => ({
        ...line,
        parsedJson: parseJsonLine(line),
      })),
    [page, snapshot.lines],
  );
  return (
    <>
      <Table<PreviewLine & { parsedJson?: ParsedJsonLine }>
        size="small"
        rowKey="number"
        dataSource={lines}
        pagination={false}
        locale={{ emptyText: '文件为空' }}
        expandable={{
          rowExpandable: (line) => line.parsedJson !== undefined,
          expandedRowRender: (line) => (
            <JsonLineView value={line.parsedJson?.value} />
          ),
        }}
        columns={[
          { title: '行号', dataIndex: 'number', width: 70 },
          {
            title: '原始内容（展开查看 JSON 树）',
            key: 'content',
            render: (_, line) => (
              <div>
                {!line.complete && <Tag>当前行尚未加载完整</Tag>}
                {line.complete && line.text.length > MAX_JSON_LINE_LENGTH && (
                  <Tag>长行，仅显示原文</Tag>
                )}
                {line.complete &&
                  line.text.length <= MAX_JSON_LINE_LENGTH &&
                  line.parsedJson === undefined && <Tag>仅显示原文</Tag>}
                <pre className="m-0 max-h-80 overflow-auto whitespace-pre-wrap break-all font-mono text-xs">
                  {line.text || ' '}
                </pre>
              </div>
            ),
          },
        ]}
      />
      {snapshot.lines.length > 100 && (
        <Space className="mt-3">
          <Button disabled={page === 1} onClick={() => setPage(page - 1)}>
            上一组行
          </Button>
          <span>
            第 {page} 组，共 {snapshot.lines.length} 行
          </span>
          <Button
            disabled={page * 100 >= snapshot.lines.length}
            onClick={() => setPage(page + 1)}
          >
            下一组行
          </Button>
        </Space>
      )}
    </>
  );
}

function PreviewContent({
  snapshot,
  provider,
}: {
  snapshot: PreviewSnapshot;
  provider: string;
}) {
  const isCodex = provider === 'codex';
  const [mode, setMode] = useState(isCodex ? 'conversation' : 'raw');
  return (
    <div className="space-y-4">
      {isCodex && (
        <Segmented
          aria-label="预览方式"
          value={mode}
          onChange={setMode}
          options={[
            { label: '会话', value: 'conversation' },
            { label: '原文 / JSON', value: 'raw' },
          ]}
        />
      )}
      {isCodex && mode === 'conversation' ? (
        <CodexTranscriptView snapshot={snapshot} />
      ) : (
        <PreviewLines snapshot={snapshot} />
      )}
    </div>
  );
}

export function SessionLogsPage() {
  const [provider, setProvider] = useState('');
  const [providers, setProviders] = useState<string[]>([]);
  const [cursors, setCursors] = useState(['']);
  const [nextCursor, setNextCursor] = useState('');
  const [rows, setRows] = useState<SessionLogUpload[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const listController = useRef<AbortController | null>(null);
  const [previewUpload, setPreviewUpload] = useState<SessionLogUpload | null>(
    null,
  );
  const [snapshot, setSnapshot] = useState<PreviewSnapshot | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const previewSession = useRef<PreviewSession | null>(null);
  const downloadController = useRef<AbortController | null>(null);
  const [downloadingId, setDownloadingId] = useState('');
  const cursor = cursors[cursors.length - 1];

  const closePreview = useCallback(() => {
    previewSession.current?.controller?.abort();
    previewSession.current = null;
    setPreviewUpload(null);
    setSnapshot(null);
    setPreviewError('');
    setPreviewLoading(false);
  }, []);

  // Refresh intentionally reruns the same provider/cursor request.
  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh is the explicit retry/refresh trigger.
  useEffect(() => {
    const controller = new AbortController();
    listController.current = controller;
    setLoading(true);
    setError('');
    setRows([]);
    setNextCursor('');
    void sessionLogsApi
      .list(
        {
          provider: provider || undefined,
          pageSize: 20,
          cursor: cursor || undefined,
        },
        controller.signal,
      )
      .then((result) => {
        if (controller.signal.aborted) return;
        setRows(result.uploads ?? []);
        setProviders(result.providers ?? []);
        setNextCursor(result.nextCursor ?? '');
      })
      .catch((cause) => {
        if (!controller.signal.aborted) {
          setError(errorText(cause, 'Session 日志列表加载失败'));
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [provider, cursor, refresh]);

  useEffect(
    () => () => {
      previewSession.current?.controller?.abort();
      previewSession.current = null;
      downloadController.current?.abort();
    },
    [],
  );

  const loadPreview = async (session: PreviewSession) => {
    if (previewSession.current !== session || session.loading) {
      return;
    }
    session.loading = true;
    session.controller = new AbortController();
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const content = await sessionLogsApi.content(
        session.upload.uploadId,
        session.controller.signal,
      );
      if (previewSession.current !== session) return;
      setSnapshot(decodeCompleteSessionLog(content, session.upload.sizeBytes));
    } catch (cause) {
      if (previewSession.current === session) {
        setPreviewError(errorText(cause, '预览加载失败'));
      }
    } finally {
      session.loading = false;
      if (previewSession.current === session) setPreviewLoading(false);
    }
  };

  const openPreview = (upload: SessionLogUpload) => {
    closePreview();
    const session: PreviewSession = {
      upload,
      loading: false,
    };
    previewSession.current = session;
    setPreviewUpload(upload);
    void loadPreview(session);
  };

  const downloadLog = async (upload: SessionLogUpload) => {
    if (downloadController.current) return;
    const controller = new AbortController();
    downloadController.current = controller;
    setDownloadingId(upload.uploadId);
    try {
      const result = await sessionLogsApi.download(
        upload.uploadId,
        controller.signal,
      );
      if (controller.signal.aborted) return;
      if (
        result.upload.uploadId !== upload.uploadId ||
        result.upload.status !== 'uploaded'
      ) {
        throw new Error('下载响应异常，请重试');
      }
      startSessionLogDownload(result.downloadUrl, result.upload.fileName);
    } catch (cause) {
      if (!controller.signal.aborted) {
        message.error(errorText(cause, '完整日志下载失败，请重试'));
      }
    } finally {
      downloadController.current = null;
      if (!controller.signal.aborted) setDownloadingId('');
    }
  };

  const downloadButton = (upload: SessionLogUpload) => (
    <Button
      icon={<DownloadOutlined />}
      loading={downloadingId === upload.uploadId}
      disabled={!!downloadingId && downloadingId !== upload.uploadId}
      onClick={() => void downloadLog(upload)}
    >
      下载完整日志
    </Button>
  );

  return (
    <div className="space-y-5 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Session 日志</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          按 Provider 查询已上传的 JSONL 日志，预览会话或下载完整原始文件。
        </p>
      </div>
      <Space wrap>
        <Select
          aria-label="Provider 筛选"
          value={provider}
          style={{ width: 220 }}
          options={[
            { value: '', label: '全部 Provider' },
            ...providers.map((value) => ({ value, label: value })),
          ]}
          onChange={(value) => {
            listController.current?.abort();
            closePreview();
            setProvider(value);
            setCursors(['']);
          }}
        />
        <Button
          loading={loading}
          onClick={() => {
            listController.current?.abort();
            setCursors(['']);
            setRefresh((value) => value + 1);
          }}
        >
          刷新
        </Button>
      </Space>
      {error && (
        <Alert
          type="error"
          showIcon
          title={error}
          action={
            <Button onClick={() => setRefresh((value) => value + 1)}>
              重试
            </Button>
          }
        />
      )}
      <Table<SessionLogUpload>
        rowKey="uploadId"
        loading={loading}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 850 }}
        locale={{ emptyText: error ? '列表加载失败' : '暂无已上传日志' }}
        columns={[
          { title: 'Provider', dataIndex: 'provider', width: 140 },
          { title: '文件名', dataIndex: 'fileName', ellipsis: true },
          {
            title: '大小',
            dataIndex: 'sizeBytes',
            width: 110,
            render: formatSize,
          },
          { title: '上传者', dataIndex: 'createdBy', ellipsis: true },
          {
            title: '上传时间',
            dataIndex: 'uploadedAtUnixMs',
            width: 180,
            render: (value: number) =>
              value ? new Date(value).toLocaleString() : '—',
          },
          {
            title: '操作',
            key: 'actions',
            width: 240,
            render: (_, upload) => (
              <Space>
                <Button onClick={() => openPreview(upload)}>预览</Button>
                {downloadButton(upload)}
              </Space>
            ),
          },
        ]}
      />
      <Space>
        <Button
          disabled={cursors.length === 1 || loading}
          onClick={() => {
            listController.current?.abort();
            setCursors((value) => value.slice(0, -1));
          }}
        >
          上一页
        </Button>
        <span>第 {cursors.length} 页</span>
        <Button
          disabled={!nextCursor || loading || !!error}
          onClick={() => {
            listController.current?.abort();
            setCursors((value) => [...value, nextCursor]);
          }}
        >
          下一页
        </Button>
      </Space>
      <Drawer
        extra={previewUpload ? downloadButton(previewUpload) : undefined}
        title={
          previewUpload
            ? `${previewUpload.provider} · ${previewUpload.fileName}`
            : '日志预览'
        }
        open={previewUpload !== null}
        onClose={closePreview}
        size={900}
        destroyOnHidden
      >
        {previewLoading && (
          <Spin tip="正在获取完整 Session，加载完成后展示">
            <div className="h-24" />
          </Spin>
        )}
        {previewError && (
          <Alert
            type="error"
            showIcon
            title={previewError}
            action={
              <Button
                onClick={() => {
                  if (previewSession.current)
                    void loadPreview(previewSession.current);
                }}
              >
                重试
              </Button>
            }
          />
        )}
        {snapshot && (
          <div className="space-y-4">
            <Alert
              type="info"
              showIcon
              title="完整 Session 已加载"
              description={`已加载 ${formatSize(snapshot.bytesLoaded)} / ${formatSize(previewUpload?.sizeBytes ?? 0)}。${previewUpload?.provider === 'codex' ? '按消息与工具调用还原会话，可切换原文查看记录。' : '原文按行展示，JSON 展开用于辅助查看。'}`}
            />
            {snapshot.lines.length > 0 ? (
              <PreviewContent
                key={previewUpload?.uploadId}
                snapshot={snapshot}
                provider={previewUpload?.provider ?? ''}
              />
            ) : (
              <Empty description="文件为空" />
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
